/*
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 */

import {Report} from '@amzn/id4-mothership/com/amazon/id4/mothership/model/report/types';
import {parseInt} from 'lodash';
import {NavigateFunction} from 'react-router/dist/lib/hooks';

import {WebStageConfig} from '../../../../config/id4-portal-config';
import {SupportedRegions} from '../../../../state/app/appSlice';
import {
    getReport,
    listReportConfigurations,
    listReports
} from '../../../../utility/id4-mothership-client';
import {RouteName} from '../../../Routing/RoutingPage';
import {
    ITableResource,
    ListResourceComponentType,
    ListResourceConfiguration,
    QueryFieldComponentType,
    QueryFieldConfiguration
} from '../ITableResource';
import {IViewResource} from '../IViewResource';

/**
 * Problem resource config.
 */
export class ReportResourceConfig implements ITableResource<Report>, IViewResource<Report> {

    /**
     * Navigate function used to navigate the user to different pages.
     * @private
     */
    private readonly navigate: NavigateFunction;

    /**
     * Constructor for class.
     * @param navigate the navigate function.
     */
    constructor(navigate: NavigateFunction) {
        this.navigate = navigate;
    }

    async queryFields(webStageConfig: WebStageConfig, region: SupportedRegions): Promise<QueryFieldConfiguration[]> {
        let reportConfigurations = await listReportConfigurations(webStageConfig, region);
        return [
            {
                fieldName: 'reportConfigurationId',
                required: true,
                componentType: QueryFieldComponentType.SELECT,
                label: 'Report Configuration ID',
                valueRetrieve: () => {
                    return reportConfigurations.map(reportConfig => reportConfig.reportConfigurationId).sort();
                }
            },
            {
                fieldName: 'problemId',
                required: false,
                componentType: QueryFieldComponentType.TEXT,
                label: 'Problem ID'
            },
            {
                fieldName: 'reportState',
                required: true,
                componentType: QueryFieldComponentType.SELECT,
                label: 'Report State',
                valueRetrieve: () => {
                    return ['CLOSED', 'PENDING', 'UPDATING', 'CREATED', 'FAILED'];
                }
            },
            {
                fieldName: 'creationTimeEpochMs',
                required: true,
                componentType: QueryFieldComponentType.DATETIME,
                label: 'Start'
            },
            {
                fieldName: 'creationEndTimeEpochMs',
                required: true,
                componentType: QueryFieldComponentType.DATETIME,
                label: 'End'
            }
        ];
    }


    putResource(webStageConfig: WebStageConfig, region: SupportedRegions, resource: Report): Promise<Report> {
        throw new Error('Method not implemented.');
    }

    initializeDefaultObject(): Report {
        throw new Error('Method not implemented.');
    }

    async retrieveResources(webStageConfig: WebStageConfig, selectedRegion: SupportedRegions, query: Record<string, string>): Promise<Report[]> {
        const fourteenDaysInMs = 14 * 24 * 60 * 60 * 1000;
        if (query) {
            if (query['creationTimeEpochMs'] && query['creationEndTimeEpochMs']) {
                let start = parseInt(query['creationTimeEpochMs']);
                let end = parseInt(query['creationEndTimeEpochMs']);
                if (start > end) {
                    throw new Error('Start time must be before end time');
                }
                // Range between start and end time must not be greater than 14 days
                if (end - start > fourteenDaysInMs) {
                    throw new Error('Time range must be less than 14 days');
                }
            }
        }
        console.log('Awaiting List Reports');
        return await listReports(webStageConfig, selectedRegion, query);
    }


    retrieveResource(webStageConfig: WebStageConfig, region: SupportedRegions, resourceId: string): Promise<Report> {
        return getReport(webStageConfig, region, {reportId: resourceId});
    }

    async deleteResource(webStageConfig: WebStageConfig, region: SupportedRegions, resource: Report) {
    }

    navigateViewResource(resource: Report): void {
        this.navigate(`${RouteName.REPORTS}/${resource.reportId}`);
    }

    navigateTableView(): void {
        this.navigate(RouteName.REPORTS);
    }

    async listResourceConfiguration(webStageConfig: WebStageConfig, selectedRegion: SupportedRegions): Promise<Record<string, ListResourceConfiguration<Report>>> {
        return {
            reportId: {
                componentType: ListResourceComponentType.TEXT,
                displayName: 'Identifier',
                onClick: id => this.navigate(RouteName.REPORTS + `/${id}`),
                maxValueLength: 15
            },
            problemIds: {
                componentType: ListResourceComponentType.TEXT,
                displayName: 'Problem IDs',
                onClick: (problemId) => this.navigate(RouteName.PROBLEMS + `/${problemId}`),
                maxValueLength: 100
            },
            reportState: {
                componentType: ListResourceComponentType.TAG,
                displayName: 'State',
                styleRetrieve: p => p.reportState === 'CREATED' ? 'success' : p.reportState === 'FAILED' ? 'error' : 'neutral',
                sortable: true
            },
            reportConfigurationId: {
                componentType: ListResourceComponentType.TEXT,
                displayName: 'Report Configuration ID',
                onClick: (id) => this.navigate(RouteName.REPORT_CONFIGURATIONS + `/${id}`),
                sortable: true
            },
            updateTimeEpochMs: {
                componentType: ListResourceComponentType.TIME,
                displayName: 'Updated',
                sortable: true
            },
        };
    }


    resourceName(): string {
        return 'Report';
    }

    searchTitle(): string {
        return 'Search Reports';
    }

    getId(resource: Report): string {
        return resource.reportId;
    }
}
